import axios from 'axios';

// baseURL을 따로 export
const { REACT_APP_API_BASE } = process.env;

const api = axios.create({
  baseURL: REACT_APP_API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,  // ★ 쿠키 전송
});

// ====== in-memory access token ======
let accessToken = null;
export const setAccessToken = (t) => { accessToken = t; };
export const clearAccessToken = () => { accessToken = null; };

// ====== refresh lock/queue (동시 401 방지) ======
let isRefreshing = false;
let queue = [];
const flush = (ok) => { queue.forEach(r => r(ok)); queue = []; };
const waitRefresh = () => new Promise(res => queue.push(res));

// 실제 리프레시 호출
// /knouUser/auth/refresh를 호출해서 새 accessToken을 setAccessToken()으로 메모리에 저장하는 역할
async function callRefresh() {
  try {
    const res = await api.post('/knouUser/auth/refresh'); // refresh 쿠키로 새 access 발급
    const newAccess =
      res?.data?.data?.accessToken || res?.data?.accessToken; // 응답 포맷 둘 다 대응
    if (!newAccess) return false;
    setAccessToken(newAccess);  // accessToken 등록
    return true;
  } catch {
    clearAccessToken();
    return false;  // 실패 → 로그인 필요
  }
}

// ====== 요청 인터셉터: Authorization 헤더 주입 ======
api.interceptors.request.use((config) => {
  const url = config?.url || '';
  const isAuth = url.includes('/knouUser/auth/');
  if (!isAuth && accessToken) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${accessToken}`;  // accessToken을 Authorization Bearer로 등록
    config._sentAuth = true; // ✅ 추가: 인증이 필요한 요청이었음을 표시
  }
  return config;
});

// ====== 응답 인터셉터: 401 처리 ======
api.interceptors.response.use(
  // 정상 응답은 그대로 통과
  (res) => res,
  // 에러 응답은 err로 들어와서 여기서 처리
  async (err) => {
    const { response, config } = err;
    const status = response?.status;
    const url = config?.url || '';


    // 로그인/리프레시/로그아웃 경로는 제외 (무한루프 방지)
    // refresh 호출 자체가 401이면 여기서는 refresh를 또 refresh 하려 하지 않고(또는 제외되어) 그냥 실패로 빠지게 설계하는 게 정상
    const isAuth = url.includes('/knouUser/auth/');
    // 401이 아니면 여기서 처리할 게 없으니 원래 에러를 그대로 던짐
    if (status !== 401 || isAuth) throw err;


    // 이미 한 번 재시도한 케이스는 원래 에러를 그대로 던짐
    if (config._retry) throw err;


    // 이미 다른 요청이 refresh 중이면 큐 대기
    /*
    어떤 요청 A가 401을 맞고 refresh를 시작하면 isRefreshing=true
    그 순간 다른 요청 B,C도 401을 맞으면,
    B,C는 refresh를 또 치지 않고 waitRefresh()로 대기
    refresh 결과(ok=true/false)를 받으면
    성공이면 자기 요청을 재시도
    실패면 원래 에러를 그대로 던짐
    */
    if (isRefreshing) {
      const ok = await waitRefresh();  // 다른 요청이 리프레시 중 → 결과 대기
      if (!ok) throw err;
      config._retry = true;
      return api(config);  // 원래요청 재시도
    }


    // 내가 첫 401이면 refresh를 실행
    isRefreshing = true;
    try {
      const ok = await callRefresh();  // 리프레시 1회
      flush(ok);  // 큐에 대기 중인 요청들에게 결과를 풀어줌(대기자들이 재시도할지 실패할지 결정)

      if (!ok) {
        // ✅ 추가: "인증헤더를 붙였던 요청"일 때만 강제 로그아웃 트리거
        if (config?._sentAuth) {
          window.dispatchEvent(
            new CustomEvent('auth:logout', { detail: { reason: 'refresh_failed' } })
          );
        }
        throw err;
      }
      
      config._retry = true;
      return api(config);  // 원래요청 재시도
    } finally {
      isRefreshing = false;
    }
  }
);

export default api;
